import type { SimpleUser, UserGroupType, UserExtended } from '@/contexts/shared/users/Users.interfaces'
import type { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import type { EvalScaleType, PublicType } from '@/interfaces/Scale.interfaces'
import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type { Pagination } from '@/interfaces/Pagination.interfaces'
import type {
  DatatableGroupByBase,
  DatatableGroupByOptions,
  DatatableSortingOrderBy,
} from '@/components/NewDesignSystem/NdsDatatable/NdsDatatable.interfaces'
import type { FilterOperator, FilterPredicates } from '@/contexts/shared/filters-predicate/FiltersPredicate.interfaces'
import type { SimpleId } from '@/interfaces/Shared.interfaces'

export interface AssignedManager {
  id: number
  label: string
  type: UserGroupType
  email: string | null
}

export interface AssignedManagers {
  count: number
  list: AssignedManager[]
}

export enum EvaluationStatus {
  APPROVED = 'APPROVED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  CHALLENGED = 'CHALLENGED',
}

export enum CampaignPerimeterStatus {
  INIT = 'INIT',
  IN_PROGRESS = 'IN_PROGRESS',
  EVAL_SUBMITED = 'EVAL_SUBMITED',
  EVAL_VALIDATED = 'EVAL_VALIDATED',
  FINISHED = 'FINISHED',
}
export enum CampaignTabStatus {
  ONGOING = 'ONGOING',
  SUBMITTED = 'SUBMITTED',
  CLOSED = 'CLOSED',
}

export enum QuestionnaireAnswerStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
}

export enum EvaluationPerimeterType {
  INTERNAL = 'INTERNAL',
  PROVIDER = 'PROVIDER',
  Value3RDPARTY = '3RDPARTY',
  APPLICATION = 'APPLICATION',
}

export enum EvaluationAssociatedItemsState {
  NON_APPLICABLE = 'NON_APPLICABLE',
  APPLICABLE = 'APPLICABLE',
  REQUIRED = 'REQUIRED',
}

export enum EvaluationAssociatedObjectType {
  REMEDIATIONS = 'REMEDIATIONS',
  MEASURES = 'MEASURES',
}

export enum EvaluationStatusTab {
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
  VALIDATED = 'VALIDATED',
}

export interface EvaluationAnswer {
  id: number
  evaluator?: SimpleUser
  counter_evaluator?: SimpleUser
  control_id?: number
  perimeter_id?: number
  campaign_id?: number
  campaign_name?: string
  eval_level_id?: number
  counter_eval_level_id?: number
  percentage?: number
  counter_percentage?: number
  remediations?: EvaluationRemediation[]
  perimeter?: EvaluationPerimeter
  measures?: EvaluationMeasure[]
  has_counter_eval?: boolean
  files?: EvaluationFile[]
  url_proof?: string
  comments?: string
  counter_comments?: string
  score?: number
  date_created?: string
  color?: string
  level_name?: string
  eval_na?: string
  counter_eval_na?: string
  counter_eval_level?: EvaluationLevel
  status?: EvaluationStatus
  challenge_user?: SimpleUser
  challenge_date?: string
}

export interface EvaluationRemediation {
  id: number
  identifier?: string
  name: string
  edit_right?: boolean
  view_right?: boolean
  delete_right?: boolean
  status?: ActionStatus
  completion?: number
  perimeter_id?: number
  perimeter?: EvaluationPerimeter
  ref?: string
  forked_solution_id?: number
  controls?: object[]
  measure_name?: string
  solution_name?: string
  action_type?: string
  controls_?: object[]
}

export interface EvaluationPerimeter {
  id?: number
  name?: string
  identifier?: string
  type?: EvaluationPerimeterType
}

export interface CampaignPerimeter extends EvaluationPerimeter {
  campaign: string
  campaign_id: number
  has_counter_eval: boolean
  status: CampaignPerimeterStatus
  q_answer_status?: string
  submitted_date: string
  priority: number
  has_eval_right: boolean
  has_review_right: boolean
  status_color: string
  is_manager: boolean
  completion: number
  nb_controls: number
}

export interface CampaignPerimeterAssigneesBody {
  campaign_id: number
  perimeter_id: number
  users: SimpleId[]
  groups: SimpleId[]
}

export interface EvaluationMeasure {
  id: number
  name: string
  edit_right?: boolean
  view_right?: boolean
  delete_right?: boolean
  perimeter_id?: number
  ref?: string
  forked_solution_id?: number
  controls?: object[]
  perimeter?: EvaluationPerimeter
  is_private?: boolean
  controls_?: object[]
  consume_perimeters?: EvaluationPerimeter[]
}

export interface EvaluationFile {
  id?: number
  filename?: string
  type?: string
  size?: number
  delete_right?: boolean
  view_right?: boolean
  date_created?: string
}

export interface EvaluationLevel {
  id: number
  eval_scale_id?: number
  name_fr?: string
  name_en?: string
  name: string
  description?: string
  description_fr?: string | null
  description_en?: string | null
  color: string
  low_value?: number
  high_value?: number
  remediation_state?: EvaluationAssociatedItemsState
  file_state?: EvaluationAssociatedItemsState
  measure_state?: EvaluationAssociatedItemsState
  comment_required?: boolean | null
}

export enum CampaignType {
  REGULAR = 'REGULAR',
}

export interface EvalScale {
  id: number
  name: string
  name_fr: string
  name_en: string
  identifier: string
  description: string
  description_fr: string | null
  description_en: string | null
  eval_levels: EvaluationLevel[]
  edit_right: boolean
  view_right: boolean
  delete_right: boolean
  edit_right_regular: boolean
  type: EvalScaleType
  public: boolean
  hide_scale: boolean
  hide_right: boolean
  public_type: PublicType
  availability: number | null
  integrity: number | null
  confidentiality: number | null
  proof: number | null
  custom_criteria: string | null
  has_prevent_delete: boolean | null
}

export interface CampaignProgram {
  id: number
  identifier: string
  name: string
}

export interface CampaignPolicyVersion {
  id: number
  policy_id: number
  name: string
  version_number: number
  identifier: string
}

export interface CampaignPolicy {
  id: number
  name: string
  public: boolean
  identifier: string
}

export interface CampaignQuestionnaire {
  id: number
  name: string
  identifier: string
}

export interface CampaignContribInfoResponse {
  objects?: {
    campaigns?: Campaign[]
  }
}

export interface Campaign {
  id: number
  name: string
  eval_scale: EvalScale
  non_applicable: boolean
  force_comment_on_non_applicable_answer?: boolean | null
  description: string
  status: string
  date_created: string
  tab_status: string
  type: CampaignType
  perimeter: CampaignPerimeter
  tag: string | null
  limit_date_evaluation: string | null
  start_date: string
  program_id: number
  program: CampaignProgram
  isp_project_id: number
  completion: number
  policy_version: CampaignPolicyVersion
  policy: CampaignPolicy
  questionnaire: CampaignQuestionnaire | null
  edit_right: boolean
  export_right: boolean
}

export interface EvaluationPreviousAnswer {
  date_created: string
  control_id: number
  campaign_name: string
  campaign_id: number
  percentage?: number
  eval_level_color: string
  eval_level_name: string
  remediations?: EvaluationRemediation[]
  measures?: EvaluationMeasure[]
  score: number
  comments?: string
  files?: EvaluationFile[]
  url_proof?: string
  perimeter: EvaluationPerimeter
  eval_na: boolean
}

export interface EvaluationComment {
  id: number
  comment: string
  timestamp: string
  user?: UserExtended | null
  from_me: boolean
}

export enum CampaignsEvaluationsDatatableColumnId {
  CAMPAIGN = 'campaign',
  PERIMETER = 'perimeter',
  QUESTIONNAIRE = 'questionnaire',
  POLICY = 'policy',
  CONTRIBUTORS = 'contributors',
  MANAGERS = 'managers',
  PROGRESSION = 'progression',
  STATUS = 'status',
  END_DATE = 'end_date',
  ACTIONS = 'actions',
}

export enum CampaignUserGroupType {
  USER = 'USER',
  GROUP = 'GROUP',
}

export interface CampaignUserGroupKey {
  id: number
  type: CampaignUserGroupType
}

export interface CampaignUserGroup extends CampaignUserGroupKey {
  label: string
  email: string | null
}

export enum EvaluationsGroupedItemStatus {
  IN_PREPARATION = 'IN_PREPARATION',
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
  VALIDATED = 'VALIDATED',
}

export enum EvaluationsGroupBy {
  CAMPAIGN = 'CAMPAIGN',
  PERIMETER = 'PERIMETER',
  POLICY = 'POLICY',
  QUESTIONNAIRE = 'QUESTIONNAIRE',
  CONTRIBUTOR = 'CONTRIBUTOR',
  MANAGER = 'MANAGER',
  STATUS = 'STATUS',
}

export enum EvaluationsSortField {
  CAMPAIGN = 'CAMPAIGN',
  PERIMETER = 'PERIMETER',
  STATUS = 'STATUS',
  VALIDATION_DATE = 'VALIDATION_DATE',
}

export interface EvaluationsGroupedFilter {
  search?: FilterPredicates<string, FilterOperator.CONTAINS>
  campaignIds?: FilterPredicates<number, FilterOperator.IN>
  perimeterIds?: FilterPredicates<number, FilterOperator.IN>
  questionnaireIds?: FilterPredicates<number | null, FilterOperator.IN>
  policyIds?: FilterPredicates<number | null, FilterOperator.IN>
  statuses?: FilterPredicates<EvaluationsGroupedItemStatus, FilterOperator.IN>
  contributors?: FilterPredicates<CampaignUserGroupKey | null, FilterOperator.IN>
  managers?: FilterPredicates<CampaignUserGroupKey | null, FilterOperator.IN>
}

export interface EvaluationsGroupedBody {
  evaluation_filter?: EvaluationsGroupedFilter
  sort_field?: EvaluationsSortField
  sort_order?: DatatableSortingOrderBy
  group_by?: DatatableGroupByOptions<EvaluationsGroupBy>
  pagination?: Pagination
}

export interface EvaluationsGroupedItemPolicy {
  id: number
  identifier: string | null
  name: TranslatedFields
}

export interface EvaluationsGroupedItemQuestionnaire {
  id: number
  identifier: string | null
  name: TranslatedFields
}

export interface EvaluationsGroupedItemCampaign {
  id: number
  name: TranslatedFields
  policy: EvaluationsGroupedItemPolicy | null
  questionnaire: EvaluationsGroupedItemQuestionnaire | null
}

export enum EvaluationsGroupedItemPerimeterType {
  INTERNAL = 'INTERNAL',
  APPLICATION = 'APPLICATION',
  PROVIDER = 'PROVIDER',
}

export interface EvaluationsGroupedItemPerimeter {
  id: number
  identifier: string
  name: TranslatedFields
  type: EvaluationsGroupedItemPerimeterType | null
}

export interface EvaluationsGroupedItem {
  campaign: EvaluationsGroupedItemCampaign
  perimeter: EvaluationsGroupedItemPerimeter
  status: EvaluationsGroupedItemStatus
  progression: number
  contributors: CampaignUserGroup[]
  managers: CampaignUserGroup[]
  validationDate: string | null
}

export type EvaluationsGrouped = DatatableGroupByBase<EvaluationsGroupedItem>

export enum CampaignsDatatableColumnId {
  NAME = 'name',
  PERIMETER = 'perimeter',
  QUESTIONNAIRE = 'questionnaire',
  POLICY = 'policy',
  MANAGERS = 'managers',
  PROGRESSION = 'progression',
  SCORE = 'score',
  STATUS = 'status',
  END_DATE = 'end_date',
  ACTIONS = 'actions',
}

export enum CampaignsGroupedItemStatus {
  IN_PREPARATION = 'IN_PREPARATION',
  LAUNCHED = 'LAUNCHED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
}

export enum CampaignsGroupBy {
  PERIMETER = 'PERIMETER',
  POLICY = 'POLICY',
  QUESTIONNAIRE = 'QUESTIONNAIRE',
  MANAGER = 'MANAGER',
  STATUS = 'STATUS',
}

export enum CampaignsSortField {
  NAME = 'NAME',
  POLICY = 'POLICY',
  QUESTIONNAIRE = 'QUESTIONNAIRE',
  STATUS = 'STATUS',
  END_DATE = 'END_DATE',
}

export interface CampaignsGroupedFilter {
  search?: FilterPredicates<string, FilterOperator.CONTAINS>
  perimeterIds?: FilterPredicates<number | null, FilterOperator.IN>
  questionnaireIds?: FilterPredicates<number | null, FilterOperator.IN>
  policyIds?: FilterPredicates<number | null, FilterOperator.IN>
  statuses?: FilterPredicates<CampaignsGroupedItemStatus, FilterOperator.IN>
  managers?: FilterPredicates<CampaignUserGroupKey | null, FilterOperator.IN>
}

export interface CampaignsGroupedBody {
  campaign_filter?: CampaignsGroupedFilter
  sort_field?: CampaignsSortField
  sort_order?: DatatableSortingOrderBy
  group_by?: DatatableGroupByOptions<CampaignsGroupBy>
  pagination?: Pagination
}

export interface CampaignsGroupedItemPolicy {
  id: number
  identifier: string | null
  name: TranslatedFields
}

export interface CampaignsGroupedItemQuestionnaire {
  id: number
  identifier: string | null
  name: TranslatedFields
}

export enum CampaignsGroupedItemPerimeterType {
  INTERNAL = 'INTERNAL',
  APPLICATION = 'APPLICATION',
  PROVIDER = 'PROVIDER',
  TAG = 'TAG',
  TREE = 'TREE',
}

export interface CampaignsGroupedItemPerimeter {
  id: number
  identifier: string | null
  name: TranslatedFields
  type: CampaignsGroupedItemPerimeterType | null
}

export interface CampaignsGroupedItem {
  id: number
  name: TranslatedFields
  status: CampaignsGroupedItemStatus
  perimeter: CampaignsGroupedItemPerimeter | null
  managers: CampaignUserGroup[]
  progression: number
  endDate: string | null
  policy: CampaignsGroupedItemPolicy | null
  questionnaire: CampaignsGroupedItemQuestionnaire | null
  score: number | null
}

export type CampaignsGrouped = DatatableGroupByBase<CampaignsGroupedItem>
